<template>
  <div>
    <div class="d-flex navigate-btn">
      <v-btn
        v-if="tab > 0"
        class="card-btn card-left-btn"
        :style="`background-color: ${config.main_color} !important`"
        @click="navigateCard('previous', message_id)"
        aria-label="info-card-navigate-left-btn"
      >
        <v-icon color="#ffffff">mdi-chevron-left</v-icon>
      </v-btn>
      <div></div>
      <v-btn
        v-if="tab < message.cards.length - 1"
        class="card-btn card-right-btn"
        :style="`background-color: ${config.main_color} !important`"
        @click="navigateCard('next', message_id)"
        aria-label="info-card-navigate-right-btn"
      >
        <v-icon color="#ffffff">mdi-chevron-right</v-icon>
      </v-btn>
    </div>
    <div class="d-flex cards-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
      <div class="chat-widget elevated-card cards" v-for="(card, card_i) in cards" :key="card_i" :eager="true">
        <div class="d-flex" style="flex-direction: column">
          <!-- Match point -->
          <div v-if="card.match_pct">
            <div class="match-text d-flex">{{ relevantMapper(card.match_pct) }}</div>
            <div>
              <v-progress-linear :value="card.match_pct" background-color="rgba(13, 85, 165, 0.24)" style="height: 0.5rem"></v-progress-linear>
            </div>
          </div>

          <!-- Image -->
          <div style="height: 10.25rem; width: 18.1825rem">
            <v-img :src="card.image_url" class="informative-card-image" :alt="card.title" :eager="true" :style="card.match_pct ? '' : 'border-radius: 1rem 1rem 0 0'">
              <template v-slot:placeholder>
                <v-skeleton-loader type="image" />
              </template>
            </v-img>
          </div>
          <div class="informative-card-detail" v-if="visibility">
            <div class="informative-card-subtitle">{{ card.subtitle }}</div>
            <div class="d-flex" style="align-items: flex-start; justify-content: space-between">
              <span class="informative-card-title">{{ card.title }}</span>
              <v-icon v-if="card.is_awarded" size="36" color="#f5cd3f">mdi-seal</v-icon>
            </div>
            <v-expand-transition hide-on-leave>
              <div>
                <!-- Tags -->
                <div class="d-flex" style="flex-wrap: wrap; padding-bottom: 0.25rem">
                  <div class="card-tags" v-for="(tag, i) in card.tags" :key="i">{{ tag }}</div>
                </div>

                <!-- Client List -->
                <div class="list-small-text d-flex" style="padding-bottom: 0.5rem">
                  <div style="color: #1b1b1b; margin-right: 0.25rem">{{ $vuetify.locale.t('$vuetify.__Cards.client_list') }}:</div>
                  <div v-for="(client, i) in card.client_list" :key="i">
                    <div v-if="i < 2" class="d-flex">
                      <span v-if="i > 0" style="padding-right: 0.25rem">,</span>
                      <a :href="client.url" target="_blank" class="ellipsis-text" style="display: block; max-width: 75px; color: #0d55a5">{{ client.title }}</a>
                    </div>
                    <div v-if="i === 2" style="color: #828282">+{{ card.client_list.length - 2 }}</div>
                  </div>
                </div>

                <!-- Success Cases -->
                <div class="list-small-text d-flex" style="padding-bottom: 0.75rem">
                  <div style="color: #1b1b1b; margin-right: 0.25rem">{{ $vuetify.locale.t('$vuetify.__Cards.success_cases') }}:</div>
                  <div v-for="(success_case, i) in card.success_cases" :key="i">
                    <div v-if="i < 2" class="d-flex">
                      <span v-if="i > 0" style="padding-right: 0.25rem">,</span>
                      <a :href="success_case.url" target="_blank" class="ellipsis-text" style="display: block; max-width: 62.5px; color: #0d55a5">{{ success_case.title }}</a>
                    </div>
                    <div v-if="i === 2" style="color: #828282">+{{ card.success_cases.length - 2 }}</div>
                  </div>
                </div>

                <!-- Button -->
                <div v-if="tab >= card_i - 1 && tab <= card_i + 1">
                  <div v-for="(button, btn_i) in card.buttons" :key="btn_i">
                    <div v-if="btn_i < item_showed[card_i]">
                      <button
                        v-ripple
                        class="informative-card-button hover-btn text-none white--text"
                        style="margin-top: 0.5rem"
                        :style="`background-color: ${config.main_color}`"
                        @click="click(button)"
                      >
                        {{ button.title }}
                      </button>
                    </div>
                    <v-btn
                      v-else-if="btn_i === item_showed[card_i]"
                      class="informative-card-button text-none white--text"
                      style="width: 3rem; margin-top: 0.5rem"
                      :style="`background-color: ${config.main_color}`"
                      variant="outlined"
                      @click="addItem(card_i)"
                      aria-label="info-card-show-more-item-btn"
                    >
                      <v-icon class="plus-btn">mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </div>
      <div style="min-width: 2.25rem"></div>
    </div>
  </div>
</template>

<script setup>
import global_config from '@/../config.json'
import { onMounted, watch, nextTick, ref, defineProps } from 'vue'
import { useLocale } from 'vuetify'
import { useStore } from 'vuex'

// Init Modules
const { t } = useLocale()
const store = useStore()

// Data
const config = ref(global_config)
const item_showed = ref([])
const item_to_add = ref(null)
const cards = ref(message.cards)
const visibility = ref(true)
const tab = ref(0)
const xDown = ref(null)
const yDown = ref(null)

// Props
const { message, message_id } = defineProps({
  message: Object,
  message_id: String
})

// Methods
const relevantMapper = (point) => {
  let text = ''
  if (point <= 100 && point > 80) text = t('$vuetify.__Cards.highly_relevant')
  else if (point <= 80 && point > 50) text = t('$vuetify.__Cards.moderately_relevant')
  else if (point <= 50 && point >= 0) text = t('$vuetify.__Cards.slightly_relevant')
  return text
}

const navigateCard = (direction, id) => {
  var message_element = document.getElementById(id)
  if (direction === 'previous') tab.value--
  else if (direction === 'next') tab.value++

  message_element.scrollLeft = tab.value * 307
}
const click = ({ type, title, value }) => {
  if (type === 'web_url') window.open(value, '_blank')
  else store.dispatch('sendMessageToSocket', { title, value })
}
const addItem = (index) => {
  item_showed.value[index] += item_to_add.value
  visibility.value = false

  nextTick(() => {
    visibility.value = true
  })
}
const getTouches = (e) => {
  return e.touches || e.originalEvent.touches
}
const handleTouchStart = (e) => {
  const firstTouch = getTouches(e)[0]
  xDown.value = firstTouch.clientX
  yDown.value = firstTouch.clientY
}
const handleTouchMove = (e) => {
  if (!xDown.value || !yDown.value) {
    return
  }

  var xUp = e.touches[0].clientX
  var yUp = e.touches[0].clientY

  var xDiff = xDown.value - xUp
  var yDiff = yDown.value - yUp

  if (Math.abs(xDiff) > Math.abs(yDiff)) {
    if (xDiff > 0) {
      // right swipe
      if (tab.value < message.cards.length - 1) navigateCard('next', message_id)
    } else {
      // left swipe
      if (tab.value > 0) navigateCard('previous', message_id)
    }
  }

  // reset values
  xDown.value = null
  yDown.value = null
}

watch(tab, () => {
  var message_element = document.getElementById(message_id)
  message_element.scrollLeft = tab.value * 307
})

onMounted(() => {
  const { default_num_of_elements = null, default_num_of_elements_to_add = null } = message
  const default_num = default_num_of_elements ? default_num_of_elements : 2

  // set array for default no. of btn showed
  const cards_length = message.cards.length
  let showed_array = []
  for (let i = 0; i < cards_length; i++) {
    showed_array.push(default_num)
  }

  item_showed.value = showed_array
  item_to_add.value = default_num_of_elements_to_add ? default_num_of_elements_to_add : 2
})
//   watch: {
//     tab() {
//       var message_element = document.getElementById(this.message_id)
//       message_element.scrollLeft = this.tab * 307
//     }
//   },
//   mounted() {
//     const { default_num_of_elements = null, default_num_of_elements_to_add = null } = this.message
//     const default_num = default_num_of_elements ? default_num_of_elements : 2

//     // set array for default no. of btn showed
//     const cards_length = this.message.cards.length
//     let showed_array = []
//     for (let i = 0; i < cards_length; i++) {
//       showed_array.push(default_num)
//     }

//     this.item_showed = showed_array
//     this.item_to_add = default_num_of_elements_to_add ? default_num_of_elements_to_add : 2
//   }
// export default {
//   name: 'Card',
//   data() {
//     return {
//       config: global_config,
//       item_showed: [],
//       item_to_add: null,
//       cards: this.message.cards,
//       visibility: true,
//       tab: 0,

//       // swipe
//       xDown: null,
//       yDown: null
//     }
//   },
//   props: {
//     message: Object,
//     chat_id: String,
//     message_id: String
//   },
//   methods: {
//     relevantMapper(point) {
//       let text = ''
//       if (point <= 100 && point > 80) text = this.$vuetify.locale.t('$vuetify.__Cards.highly_relevant')
//       else if (point <= 80 && point > 50) text = this.$vuetify.locale.t('$vuetify.__Cards.moderately_relevant')
//       else if (point <= 50 && point >= 0) text = this.$vuetify.locale.t('$vuetify.__Cards.slightly_relevant')
//       return text
//     },

//     navigateCard(direction, id) {
//       var message_element = document.getElementById(id)
//       if (direction === 'previous') this.tab--
//       else if (direction === 'next') this.tab++

//       message_element.scrollLeft = this.tab * 307
//     },
//     click({ type, title, value }) {
//       if (type === 'web_url') window.open(value, '_blank')
//       else this.$store.dispatch('sendMessageToSocket', { title, value })
//     },
//     addItem(index) {
//       this.item_showed[index] += this.item_to_add
//       this.visibility = false

//       this.$nextTick(() => {
//         this.visibility = true
//       })
//     },
//     getTouches(e) {
//       return e.touches || e.originalEvent.touches
//     },
//     handleTouchStart(e) {
//       const firstTouch = this.getTouches(e)[0]
//       this.xDown = firstTouch.clientX
//       this.yDown = firstTouch.clientY
//     },
//     handleTouchMove(e) {
//       if (!this.xDown || !this.yDown) {
//         return
//       }

//       var xUp = e.touches[0].clientX
//       var yUp = e.touches[0].clientY

//       var xDiff = this.xDown - xUp
//       var yDiff = this.yDown - yUp

//       if (Math.abs(xDiff) > Math.abs(yDiff)) {
//         if (xDiff > 0) {
//           // right swipe
//           if (this.tab < this.message.cards.length - 1) this.$emit('update:tab', this.tab + 1)
//         } else {
//           // left swipe
//           if (this.tab > 0) this.$emit('update:tab', this.tab - 1)
//         }
//       }

//       // reset values
//       this.xDown = null
//       this.yDown = null
//     }
//   },
//   watch: {
//     tab() {
//       var message_element = document.getElementById(this.message_id)
//       message_element.scrollLeft = this.tab * 307
//     }
//   },
//   mounted() {
//     const { default_num_of_elements = null, default_num_of_elements_to_add = null } = this.message
//     const default_num = default_num_of_elements ? default_num_of_elements : 2

//     // set array for default no. of btn showed
//     const cards_length = this.message.cards.length
//     let showed_array = []
//     for (let i = 0; i < cards_length; i++) {
//       showed_array.push(default_num)
//     }

//     this.item_showed = showed_array
//     this.item_to_add = default_num_of_elements_to_add ? default_num_of_elements_to_add : 2
//   }
// }
</script>

<style scope>
.card-btn {
  position: absolute;
  background-color: #ffffff !important;
  box-shadow: 0 0.0625rem 0.0625rem rgba(0, 0, 0, 0.14), 0 0.125rem 0.0625rem rgba(0, 0, 0, 0.12), 0px 0.0625rem 0.1875rem rgba(0, 0, 0, 0.2);
  padding: 0 !important;
  margin-top: 8.6875rem;
  height: 3rem !important;
}

.card-left-btn {
  border-radius: 0 0.5rem 0.5rem 0;
  left: 0;
  z-index: 100;
}

.card-right-btn {
  border-radius: 0.5rem 0 0 0.5rem;
  right: 0;
  z-index: 100;
}

.cards-container {
  flex-direction: row;
}

.cards {
  height: fit-content;
  width: 18.1875rem;
  margin-right: 1rem;
}

.match-text {
  height: 1.75rem;
  padding: 0.5rem 0 0.25rem 0;
  justify-content: center;
  color: #009ddf;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
}

.v-progress-linear__determinate {
  background: linear-gradient(128deg, rgba(13, 85, 165, 0.92) 0%, rgba(0, 157, 223, 0.83) 100%);
}

.informative-card-image {
  height: 10.25rem;
  width: 100%;
}

.informative-card-detail {
  padding: 0 1.5rem 1rem 1.5rem;
}

.informative-card-title {
  max-width: 15.1875rem;
  white-space: initial;
  padding-bottom: 0.25rem;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.625rem;
  color: #1a1a1a;
}

.informative-card-subtitle {
  max-width: 15.1875rem;
  white-space: initial;
  padding: 1rem 0 0.75rem 0;
  font-weight: 400;
  font-size: 0.9375rem;
  line-height: normal;
  color: #1b1b1b;
}

.informative-card-button {
  max-width: 15.1875rem;
  white-space: initial;
  font-weight: 400 !important;
  font-size: 0.9375rem !important;
  line-height: 1.5rem;
  border-radius: 0.75rem !important;
  padding: 0.625rem 1rem;
  text-align: start;
  min-height: 2.75rem !important;
}

.card-tags {
  margin: 0 0.5rem 0.5rem 0;
  border-radius: 0.25rem;
  background: rgba(0, 0, 0, 0.08);
  padding: 0.25rem 0.375rem;

  color: #1b1b1b;
  font-family: Open Sans;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: 0.00625rem;
}

.list-small-text {
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.list-item {
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.list-plus {
  justify-content: center;
  padding: 1rem 0;
}
</style>
